import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap, DollarSign, Gem, Flame, Mail, Home, ClipboardList,
  ChevronRight, ChevronDown, Gift, Users, Coins, Swords,
  MapPin, Package, BookOpen, Calendar, ShoppingBag, Star,
  Timer, Shield, Trophy, Crown, X, Check, Lock, Clock,
  Sparkles, PackageOpen
} from 'lucide-react';
import WeaponsArsenal from './store/WeaponsArsenal';
import CollectionPanel from './store/CollectionPanel';
import InboxPanel from './store/InboxPanel';
import BuyResult from './store/BuyResult';
import PurchaseHistory from './store/PurchaseHistory';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Icon mapper for checkin reward types
const REWARD_ICONS = {
  cash: <DollarSign size={16} />,
  energy: <Zap size={16} />,
  xp: <Star size={16} />,
  matt: <PackageOpen size={16} />,
  crew: <Users size={16} />,
  territory: <MapPin size={16} />,
  special: <Trophy size={16} />,
  weapon: <Swords size={16} />,
  influence: <Flame size={16} />,
  water: <Gem size={16} />,
};

// ============================================================
// TOP CURRENCY BAR
// ============================================================
const CurrencyBar = ({ currencies, onClose, onHomeClick, onInboxClick }) => (
  <div className="flex items-center justify-between px-4 py-2 bg-[#080b0f] border-b border-[#C9A84C]/15" data-testid="store-currency-bar">
    <div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-1.5">
        <Zap size={13} className="text-[#4ADE80]" />
        <span className="font-['Outfit'] text-[#4ADE80] text-xs font-semibold">{currencies.energy}/{currencies.max_energy || 100}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <DollarSign size={13} className="text-[#C9A84C]" />
        <span className="font-['Outfit'] text-[#C9A84C] text-xs font-semibold">${(currencies.cash || 0).toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Gem size={13} className="text-[#9F7AEA]" />
        <span className="font-['Outfit'] text-[#9F7AEA] text-xs font-semibold">{(currencies.water_crystals || 0).toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Flame size={13} className="text-[#F59E0B]" />
        <span className="font-['Outfit'] text-[#F59E0B] text-xs font-semibold">{(currencies.influence_points || 0).toLocaleString()}</span>
      </div>
      <button onClick={onInboxClick} className="flex items-center gap-1 hover:opacity-80 transition-opacity" data-testid="store-inbox-btn">
        <Mail size={13} className="text-[#E8E0D0]/50" />
        {currencies.inbox_count > 0 && (
          <span className="bg-[#8B1A1A] text-[#E8E0D0] text-[9px] font-bold px-1.5 py-0.5 rounded-full">{currencies.inbox_count}</span>
        )}
      </button>
    </div>
    <div className="flex items-center gap-2">
      <button onClick={onHomeClick} className="text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60 p-1" data-testid="store-home-btn">
        <Home size={16} />
      </button>
      <button onClick={onClose} className="text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60 p-1" data-testid="store-close-btn">
        <X size={16} />
      </button>
    </div>
  </div>
);

// ============================================================
// LEFT SIDEBAR NAVIGATION
// ============================================================
const SIDEBAR_ICONS = {
  daily_perks: <Gift size={14} />,
  character: <Users size={14} />,
  currency: <Coins size={14} />,
  weapons: <Swords size={14} />,
  territory: <MapPin size={14} />,
  materials: <Package size={14} />,
  collection: <BookOpen size={14} />,
  event: <Calendar size={14} />,
  backpack_matt: <PackageOpen size={14} />,
};

const StoreSidebar = ({ categories, activeSubTab, onSelectSubTab, newBadges }) => {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (catId) => {
    setExpanded(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // When a sub-tab is selected via content area, auto-expand its parent category
  useEffect(() => {
    for (const cat of categories) {
      if (cat.sub.some(s => s.id === activeSubTab)) {
        setExpanded(prev => ({ ...prev, [cat.id]: true }));
      }
    }
  }, [activeSubTab, categories]);

  return (
    <div className="w-48 shrink-0 bg-[#060910] border-r border-[#C9A84C]/10 overflow-y-auto" data-testid="store-sidebar">
      <div className="p-3">
        <p className="font-['Cormorant_Garamond'] text-[#C9A84C] text-xs tracking-widest mb-3">MATT'S STORE</p>
        {categories.map(cat => {
          const isExpanded = expanded[cat.id];
          const hasNew = newBadges?.includes(cat.id);
          return (
            <div key={cat.id} className="mb-0.5">
              <button
                onClick={() => toggleExpand(cat.id)}
                className="w-full flex items-center justify-between py-1.5 px-2 rounded hover:bg-[#C9A84C]/5 transition-colors group"
                data-testid={`store-cat-${cat.id}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-[#C9A84C]/40 group-hover:text-[#C9A84C]/70">{SIDEBAR_ICONS[cat.id]}</span>
                  <span className="font-['Outfit'] text-[10px] tracking-wider text-[#E8E0D0]/50 group-hover:text-[#E8E0D0]/80">{cat.label}</span>
                  {hasNew && <span className="font-['Outfit'] text-[7px] px-1 py-0.5 bg-[#8B1A1A]/30 text-[#FF6B6B] rounded tracking-widest">NEW</span>}
                </div>
                {isExpanded ? <ChevronDown size={10} className="text-[#E8E0D0]/20" /> : <ChevronRight size={10} className="text-[#E8E0D0]/20" />}
              </button>
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    {cat.sub.map(sub => (
                      <button
                        key={sub.id}
                        onClick={() => onSelectSubTab(sub.id)}
                        className={`w-full text-left pl-8 pr-2 py-1 rounded text-[10px] font-['Outfit'] transition-colors ${
                          activeSubTab === sub.id
                            ? 'text-[#C9A84C] bg-[#C9A84C]/10'
                            : 'text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60 hover:bg-[#E8E0D0]/5'
                        }`}
                        data-testid={`store-sub-${sub.id}`}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
      {/* VIP bar at bottom */}
      <div className="border-t border-[#C9A84C]/10 p-3 mt-2">
        <div className="flex items-center gap-1.5">
          <Crown size={12} className="text-[#C9A84C]/50" />
          <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest">VIP LEVEL</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ITEM CARD (horizontal scroll)
// ============================================================
const StoreItemCard = ({ item, onBuy, buying, payWithCash }) => {
  const isPriceUsd = item.price_type === 'usd';
  const isDual = item.price_type === 'dual';
  const showCashPrice = payWithCash && isDual;

  const priceLabel = isPriceUsd
    ? item.price_display || `$${(item.price_cents / 100).toFixed(2)}`
    : showCashPrice
      ? `$${(item.cash_price || 0).toLocaleString()}`
      : `${item.price?.toLocaleString()}`;

  const canAfford = isPriceUsd ? true
    : showCashPrice ? item.can_afford_cash
    : item.can_afford ?? (item.can_afford_crystals !== undefined ? item.can_afford_crystals : true);

  const limitReached = item.limit_reached;

  return (
    <div
      className={`shrink-0 w-[160px] bg-[#0a0e12] border rounded-lg p-3 flex flex-col transition-all ${
        item.badge === 'BEST VALUE' ? 'border-[#C9A84C]/40' :
        item.badge === 'MOST POPULAR' ? 'border-[#9F7AEA]/40' :
        'border-[#1A2535] hover:border-[#C9A84C]/20'
      }`}
      data-testid={`store-item-${item.id}`}
    >
      {item.badge && (
        <span className={`font-['Outfit'] text-[7px] tracking-widest px-1.5 py-0.5 rounded self-start mb-1.5 ${
          item.badge === 'BEST VALUE' ? 'bg-[#C9A84C]/15 text-[#C9A84C]' :
          item.badge === 'MOST POPULAR' ? 'bg-[#9F7AEA]/15 text-[#9F7AEA]' :
          item.badge === 'LOCKED' ? 'bg-[#8B1A1A]/15 text-[#8B1A1A]' :
          item.badge === 'LEGACY' ? 'bg-[#4ADE80]/15 text-[#4ADE80]' :
          'bg-[#E8E0D0]/10 text-[#E8E0D0]/50'
        }`}>{item.badge}</span>
      )}
      <h5 className="font-['Outfit'] text-[#E8E0D0] text-xs font-semibold leading-tight">{item.name}</h5>
      <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] mt-1 flex-1 leading-relaxed">{item.desc || item.ability || ''}</p>
      {item.stars && (
        <div className="flex gap-0.5 mt-1">
          {Array.from({ length: item.stars }).map((_, i) => (
            <Star key={i} size={8} className="text-[#C9A84C] fill-[#C9A84C]" />
          ))}
        </div>
      )}
      {item.role && (
        <span className="font-['Outfit'] text-[8px] tracking-widest px-1.5 py-0.5 rounded bg-[#E8E0D0]/5 text-[#E8E0D0]/40 self-start mt-1">{item.role}</span>
      )}
      {item.daily_limit && (
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px] mt-1">
          LIMIT: {item.purchased_today || 0}/{item.daily_limit}/day
        </p>
      )}
      {item.matt_line && (
        <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/20 italic text-[10px] mt-1">"{item.matt_line}"</p>
      )}
      <button
        onClick={() => onBuy(item)}
        disabled={buying || limitReached || item.locked || !canAfford}
        className={`mt-2 w-full py-1.5 rounded font-['Outfit'] text-[10px] tracking-wider border transition-all disabled:opacity-25 disabled:cursor-not-allowed ${
          isPriceUsd
            ? 'bg-[#9F7AEA]/10 border-[#9F7AEA]/30 text-[#9F7AEA] hover:bg-[#9F7AEA]/20'
            : showCashPrice
              ? 'bg-[#C9A84C]/10 border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/20'
              : 'bg-[#9F7AEA]/10 border-[#9F7AEA]/30 text-[#9F7AEA] hover:bg-[#9F7AEA]/20'
        }`}
        data-testid={`store-buy-${item.id}`}
      >
        {limitReached ? 'LIMIT REACHED' : item.locked ? 'LOCKED' : (
          <span className="flex items-center justify-center gap-1">
            {showCashPrice ? <DollarSign size={10} /> : !isPriceUsd ? <Gem size={10} /> : null}
            {priceLabel}
          </span>
        )}
      </button>
    </div>
  );
};

// ============================================================
// CHECK-IN PANEL
// ============================================================
const CheckInPanel = ({ gameId }) => {
  const [checkinData, setCheckinData] = useState(null);
  const [activeTrack, setActiveTrack] = useState('daily');
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);

  useEffect(() => { loadCheckin(); }, [gameId]);

  const loadCheckin = async () => {
    try {
      const res = await fetch(`${API}/api/store/checkin/${gameId}`);
      if (res.ok) setCheckinData(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleClaim = async (track) => {
    setClaiming(true);
    try {
      const res = await fetch(`${API}/api/store/checkin/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, track }),
      });
      if (res.ok) {
        const data = await res.json();
        setClaimResult(data);
        setTimeout(() => setClaimResult(null), 3500);
        loadCheckin();
      }
    } catch (e) { console.error(e); }
    setClaiming(false);
  };

  if (!checkinData) return <div className="p-8 text-center"><p className="text-[#E8E0D0]/20 text-xs">Loading check-in...</p></div>;

  const tracks = [
    { id: 'daily', label: 'DAILY STREET RUN', canClaim: checkinData.daily.can_claim },
    { id: 'weekly', label: '7-DAY EVENT', canClaim: checkinData.weekly.can_claim },
    { id: 'eight_week', label: '8-WEEK REWARD', canClaim: checkinData.eight_week.can_claim },
    { id: 'monthly', label: 'MONTHLY', canClaim: checkinData.monthly.can_claim },
  ];

  return (
    <div data-testid="checkin-panel">
      {/* Track tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
        {tracks.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTrack(t.id)}
            className={`shrink-0 px-3 py-1.5 rounded font-['Outfit'] text-[9px] tracking-wider border transition-all flex items-center gap-1.5 ${
              activeTrack === t.id
                ? 'border-[#C9A84C]/40 text-[#C9A84C] bg-[#C9A84C]/10'
                : 'border-[#1A2535] text-[#E8E0D0]/30 hover:text-[#E8E0D0]/60'
            }`}
            data-testid={`checkin-track-${t.id}`}
          >
            {t.label}
            {t.canClaim && <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] animate-pulse" />}
          </button>
        ))}
      </div>

      {/* Claim result toast */}
      <AnimatePresence>
        {claimResult && claimResult.success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-lg p-3 mb-4"
            data-testid="checkin-claim-result"
          >
            <p className="font-['Outfit'] text-[#C9A84C] text-xs font-semibold">{claimResult.reward_name}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {claimResult.applied?.map((a, i) => (
                <span key={i} className="font-['Outfit'] text-[#E8E0D0]/60 text-[10px] bg-[#E8E0D0]/5 px-1.5 py-0.5 rounded">{a}</span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DAILY STREET RUN TRACK */}
      {activeTrack === 'daily' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/40 italic text-sm">"Show up every day. The block remembers."</p>
            <span className="font-['Outfit'] text-[#C9A84C] text-xs">Streak: {checkinData.daily.streak}</span>
          </div>
          {checkinData.daily.streak_broken && (
            <div className="bg-[#8B1A1A]/10 border border-[#8B1A1A]/30 rounded p-2 mb-3">
              <p className="font-['Outfit'] text-[#8B1A1A] text-[10px]">Streak broken. Starting fresh.</p>
            </div>
          )}
          {/* 28-day grid: 4 rows x 7 cols */}
          <div className="grid grid-cols-7 gap-1.5 mb-4">
            {checkinData.daily.grid.map(day => (
              <div
                key={day.day}
                className={`relative rounded p-1.5 text-center transition-all ${
                  day.status === 'completed' ? 'bg-[#C9A84C]/10 border border-[#C9A84C]/20' :
                  day.status === 'current' ? 'bg-[#C9A84C]/5 border-2 border-[#C9A84C]/50 animate-pulse' :
                  day.status === 'today_claimed' ? 'bg-[#4ADE80]/5 border border-[#4ADE80]/20' :
                  'bg-[#0a0e12] border border-[#1A2535]'
                } ${day.milestone ? 'ring-1 ring-[#C9A84C]/30' : ''}`}
                data-testid={`checkin-day-${day.day}`}
              >
                {day.status === 'completed' && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#C9A84C] rounded-full flex items-center justify-center">
                    <Check size={7} className="text-[#0a0e12]" />
                  </div>
                )}
                <span className="text-[#E8E0D0]/30 text-[8px] block">{day.day}</span>
                <span className={`block mt-0.5 ${day.milestone ? 'text-[#C9A84C]' : 'text-[#E8E0D0]/20'}`}>
                  {REWARD_ICONS[day.icon] || <Gift size={14} />}
                </span>
                <span className="font-['Outfit'] text-[7px] text-[#E8E0D0]/25 mt-0.5 block truncate">{day.name}</span>
              </div>
            ))}
          </div>
          {/* Claim button */}
          {checkinData.daily.can_claim && (
            <button
              onClick={() => handleClaim('daily')}
              disabled={claiming}
              className="w-full py-2.5 rounded-lg bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/25 transition-all disabled:opacity-50"
              data-testid="checkin-claim-daily"
            >
              {claiming ? 'CLAIMING...' : 'CHECK IN — CLAIM REWARD'}
            </button>
          )}
          {!checkinData.daily.can_claim && (
            <p className="text-center font-['Outfit'] text-[#E8E0D0]/20 text-[10px] tracking-widest">
              <Clock size={10} className="inline mr-1" />
              COME BACK TOMORROW
            </p>
          )}
          {/* Milestones sidebar */}
          <div className="mt-4 border-t border-[#C9A84C]/10 pt-3">
            <p className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] tracking-widest mb-2">MILESTONES</p>
            {checkinData.daily.milestones.map(m => (
              <div key={m.day} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2">
                  <span className={m.reached ? 'text-[#C9A84C]' : 'text-[#E8E0D0]/15'}>{REWARD_ICONS[m.icon]}</span>
                  <div>
                    <span className={`font-['Outfit'] text-[10px] ${m.reached ? 'text-[#C9A84C]' : 'text-[#E8E0D0]/40'}`}>Day {m.day} — {m.name}</span>
                  </div>
                </div>
                {m.reached ? (
                  <Check size={12} className="text-[#C9A84C]" />
                ) : (
                  <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">{m.days_away}d away</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 7-DAY EVENT TRACK */}
      {activeTrack === 'weekly' && (
        <div>
          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/40 italic text-sm mb-3">"A week in the game. The streets noticed."</p>
          <div className="space-y-1.5">
            {checkinData.weekly.grid.map(day => (
              <div
                key={day.day}
                className={`flex items-center justify-between p-2 rounded border ${
                  day.status === 'completed' ? 'bg-[#C9A84C]/5 border-[#C9A84C]/20' :
                  day.status === 'current' ? 'bg-[#C9A84C]/5 border-[#C9A84C]/40' :
                  'bg-[#0a0e12] border-[#1A2535]'
                }`}
                data-testid={`checkin-weekly-${day.day}`}
              >
                <div className="flex items-center gap-2">
                  {day.status === 'completed' ? <Check size={12} className="text-[#C9A84C]" /> : <span className="text-[#E8E0D0]/20 text-xs w-3">{day.day}</span>}
                  <span className={`font-['Outfit'] text-[10px] ${day.milestone ? 'text-[#C9A84C] font-semibold' : 'text-[#E8E0D0]/50'}`}>{day.name}</span>
                </div>
                {day.status === 'current' && <span className="w-2 h-2 rounded-full bg-[#4ADE80] animate-pulse" />}
              </div>
            ))}
          </div>
          {checkinData.weekly.can_claim && (
            <button onClick={() => handleClaim('weekly')} disabled={claiming}
              className="w-full mt-3 py-2 rounded-lg bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/25 disabled:opacity-50"
              data-testid="checkin-claim-weekly">
              {claiming ? 'CLAIMING...' : 'CLAIM WEEKLY REWARD'}
            </button>
          )}
        </div>
      )}

      {/* 8-WEEK TRACK */}
      {activeTrack === 'eight_week' && (
        <div>
          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/40 italic text-sm mb-3">"Two months solid. That's loyalty."</p>
          <div className="space-y-1.5">
            {checkinData.eight_week.grid.map(week => (
              <div
                key={week.week}
                className={`flex items-center justify-between p-2 rounded border ${
                  week.status === 'completed' ? 'bg-[#C9A84C]/5 border-[#C9A84C]/20' :
                  week.status === 'current' ? 'bg-[#C9A84C]/5 border-[#C9A84C]/40' :
                  'bg-[#0a0e12] border-[#1A2535]'
                }`}
              >
                <div className="flex items-center gap-2">
                  {week.status === 'completed' ? <Check size={12} className="text-[#C9A84C]" /> : <span className="text-[#E8E0D0]/20 text-xs w-4">W{week.week}</span>}
                  <span className={`font-['Outfit'] text-[10px] ${week.milestone ? 'text-[#C9A84C] font-semibold' : 'text-[#E8E0D0]/50'}`}>{week.name}</span>
                </div>
                {week.status === 'current' && <span className="w-2 h-2 rounded-full bg-[#4ADE80] animate-pulse" />}
              </div>
            ))}
          </div>
          {checkinData.eight_week.can_claim && (
            <button onClick={() => handleClaim('eight_week')} disabled={claiming}
              className="w-full mt-3 py-2 rounded-lg bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/25 disabled:opacity-50"
              data-testid="checkin-claim-eight-week">
              {claiming ? 'CLAIMING...' : 'CLAIM WEEKLY MILESTONE'}
            </button>
          )}
        </div>
      )}

      {/* MONTHLY TRACK */}
      {activeTrack === 'monthly' && (
        <div>
          <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/40 italic text-sm mb-3">"Every month you show up. Every month the empire grows."</p>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="font-['Outfit'] text-[#E8E0D0]/40 text-[10px]">Check-ins: {checkinData.monthly.count}/{checkinData.monthly.target}</span>
              <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px]">{checkinData.monthly.month}</span>
            </div>
            <div className="h-2 bg-[#1A2535] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#C9A84C]/50 rounded-full transition-all"
                style={{ width: `${Math.min(100, (checkinData.monthly.count / checkinData.monthly.target) * 100)}%` }}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            {checkinData.monthly.milestones.map(ms => (
              <div key={ms.threshold}
                className={`flex items-center justify-between p-2 rounded border ${
                  ms.reached ? 'bg-[#C9A84C]/5 border-[#C9A84C]/20' : 'bg-[#0a0e12] border-[#1A2535]'
                }`}
              >
                <div className="flex items-center gap-2">
                  {ms.reached ? <Check size={12} className="text-[#C9A84C]" /> : <span className="text-[#E8E0D0]/20 text-[9px] w-4">{ms.threshold}</span>}
                  <span className={`font-['Outfit'] text-[10px] ${ms.reached ? 'text-[#C9A84C]' : 'text-[#E8E0D0]/50'}`}>{ms.name}</span>
                </div>
              </div>
            ))}
          </div>
          {checkinData.monthly.can_claim && (
            <button onClick={() => handleClaim('monthly')} disabled={claiming}
              className="w-full mt-3 py-2 rounded-lg bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/25 disabled:opacity-50"
              data-testid="checkin-claim-monthly">
              {claiming ? 'CLAIMING...' : 'CHECK IN'}
            </button>
          )}
        </div>
      )}

      {/* Reset timer */}
      <div className="mt-4 text-center">
        <ResetTimer seconds={checkinData.reset_in_seconds} />
      </div>
    </div>
  );
};

const ResetTimer = ({ seconds }) => {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    const t = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  return (
    <span className="font-['Outfit'] text-[#E8E0D0]/15 text-[9px] tracking-widest">
      <Timer size={9} className="inline mr-1" />
      RESETS IN: {h}h {m}m
    </span>
  );
};

// ============================================================
// MAIN STORE CONTENT AREA
// ============================================================
const StoreContent = ({ activeSubTab, gameId, currencies, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying] = useState(null);
  const [buyResult, setBuyResult] = useState(null);
  const [tabMeta, setTabMeta] = useState({});
  const [payWithCash, setPayWithCash] = useState(false);

  useEffect(() => {
    if (activeSubTab === 'checkin_bonus') return;
    setItems([]);
    loadItems();
  }, [activeSubTab, gameId]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/store/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, sub_tab: activeSubTab }),
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTabMeta(data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleBuy = async (item) => {
    if (item.price_type === 'usd') {
      // USD items go through Stripe checkout
      setBuying(item.id);
      try {
        const res = await fetch(`${API}/api/store/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ game_id: gameId, package_id: item.id, origin_url: window.location.origin }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.url) window.location.href = data.url;
        }
      } catch (e) { console.error(e); }
      setBuying(null);
      return;
    }

    setBuying(item.id);
    try {
      const currency = payWithCash ? 'cash' : 'crystals';
      const res = await fetch(`${API}/api/store/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, sub_tab: activeSubTab, item_id: item.id, currency }),
      });
      if (res.ok) {
        const data = await res.json();
        setBuyResult(data);
        setTimeout(() => setBuyResult(null), 3000);
        if (data.success) {
          onRefresh();
          loadItems();
        }
      }
    } catch (e) { console.error(e); }
    setBuying(null);
  };

  const handleBuyCrystals = async (pkgId) => {
    // Crystal packs go through Stripe
    setBuying(pkgId);
    try {
      const res = await fetch(`${API}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, package_id: pkgId, origin_url: window.location.origin }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      }
    } catch (e) { console.error(e); }
    setBuying(null);
  };

  // Check-in bonus redirects to check-in panel
  if (activeSubTab === 'checkin_bonus') {
    return <CheckInPanel gameId={gameId} />;
  }

  // Weapons arsenal tabs
  if (activeSubTab.startsWith('era_') && activeSubTab.endsWith('_arsenal')) {
    return <WeaponsArsenal gameId={gameId} eraNum={parseInt(activeSubTab.split('_')[1])} onRefresh={onRefresh} />;
  }

  // Dossier fragments tab
  if (activeSubTab === 'dossier_fragments') {
    return <CollectionPanel gameId={gameId} onRefresh={onRefresh} />;
  }

  // Purchase history tab
  if (activeSubTab === 'purchase_history') {
    return <PurchaseHistory gameId={gameId} />;
  }

  // Water crystals has special UI
  if (activeSubTab === 'water_crystals') {
    return (
      <div>
        <h3 className="font-['Cormorant_Garamond'] text-[#9F7AEA] text-lg font-bold mb-1">WATER CRYSTALS</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mb-4">Premium currency. Power up your empire.</p>
        <BuyResult result={buyResult} />
        {loading ? <p className="text-[#E8E0D0]/20 text-xs animate-pulse">Loading...</p> : (
          <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide">
            {items.filter(p => p.crystals != null).map(pkg => (
            <div key={pkg.id} className={`shrink-0 w-[150px] bg-[#0a0e12] border rounded-lg p-3 flex flex-col text-center ${
              pkg.badge === 'BEST VALUE' ? 'border-[#C9A84C]/40' : pkg.badge === 'MOST POPULAR' ? 'border-[#9F7AEA]/40' : 'border-[#1A2535]'
            }`} data-testid={`store-crystal-${pkg.id}`}>
              {pkg.badge && (
                <span className={`font-['Outfit'] text-[7px] tracking-widest px-1.5 py-0.5 rounded self-center mb-2 ${
                  pkg.badge === 'BEST VALUE' ? 'bg-[#C9A84C]/15 text-[#C9A84C]' : 'bg-[#9F7AEA]/15 text-[#9F7AEA]'
                }`}>{pkg.badge}</span>
              )}
              <Gem size={20} className="text-[#9F7AEA] mx-auto" />
              <span className="font-['Outfit'] text-[#E8E0D0] text-sm font-bold mt-1">{pkg.crystals.toLocaleString()}</span>
              {pkg.bonus > 0 && (
                <span className="font-['Outfit'] text-[#4ADE80] text-[9px]">+{pkg.bonus.toLocaleString()} bonus</span>
              )}
              <span className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-1">{pkg.name}</span>
              <button
                onClick={() => handleBuyCrystals(pkg.id)}
                disabled={buying === pkg.id}
                className="mt-2 w-full py-1.5 rounded bg-[#9F7AEA]/10 border border-[#9F7AEA]/30 text-[#9F7AEA] font-['Outfit'] text-[10px] tracking-wider hover:bg-[#9F7AEA]/20 disabled:opacity-50"
                data-testid={`store-buy-crystal-${pkg.id}`}
              >
                {pkg.price_display}
              </button>
            </div>
          ))}
          </div>
        )}
        <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px] mt-2 tracking-widest">LOYALTY POINTS EARNED ON ALL PURCHASES</p>
      </div>
    );
  }

  if (loading) return <div className="p-8 text-center"><p className="text-[#E8E0D0]/20 text-xs animate-pulse">Loading...</p></div>;

  if (tabMeta.note) {
    return (
      <div className="p-8 text-center">
        <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/30 italic">{tabMeta.note}</p>
      </div>
    );
  }

  return (
    <div>
      <BuyResult result={buyResult} />
      {/* Currency Toggle */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-1 bg-[#080b0f] rounded-full p-0.5 border border-[#1A2535]" data-testid="currency-toggle">
          <button
            onClick={() => setPayWithCash(false)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full font-['Outfit'] text-[10px] tracking-wider transition-all ${
              !payWithCash ? 'bg-[#9F7AEA]/15 text-[#9F7AEA] border border-[#9F7AEA]/30' : 'text-[#E8E0D0]/30 border border-transparent hover:text-[#E8E0D0]/50'
            }`}
            data-testid="currency-toggle-crystals"
          >
            <Gem size={10} /> CRYSTALS
          </button>
          <button
            onClick={() => setPayWithCash(true)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full font-['Outfit'] text-[10px] tracking-wider transition-all ${
              payWithCash ? 'bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30' : 'text-[#E8E0D0]/30 border border-transparent hover:text-[#E8E0D0]/50'
            }`}
            data-testid="currency-toggle-cash"
          >
            <DollarSign size={10} /> CASH
          </button>
        </div>
        <span className="font-['Outfit'] text-[8px] tracking-wider text-[#E8E0D0]/20">
          {payWithCash ? `BALANCE: $${(currencies.cash || 0).toLocaleString()}` : `BALANCE: ${(currencies.water_crystals || 0).toLocaleString()} crystals`}
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide">
        {items.map(item => (
          <StoreItemCard key={item.id} item={item} onBuy={handleBuy} buying={buying === item.id} payWithCash={payWithCash} />
        ))}
      </div>
      {items.length === 0 && (
        <p className="text-center text-[#E8E0D0]/20 text-xs py-8">No items available.</p>
      )}
    </div>
  );
};

// BuyResult, WeaponsArsenal, CollectionPanel, InboxPanel imported from ./store/

// ============================================================
// DEAL OF THE DAY HERO CARD
// ============================================================
const DealOfTheDay = ({ gameId, onRefresh }) => {
  const [deal, setDeal] = useState(null);
  const [expiresIn, setExpiresIn] = useState(0);
  const [buying, setBuying] = useState(false);
  const [bought, setBought] = useState(false);

  useEffect(() => { loadDeal(); }, [gameId]);

  useEffect(() => {
    if (expiresIn <= 0) return;
    const t = setInterval(() => setExpiresIn(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [expiresIn]);

  const loadDeal = async () => {
    try {
      const res = await fetch(`${API}/api/store/deal-of-the-day/${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setDeal(data.deal);
        setExpiresIn(data.expires_in);
        setBought(data.deal?.already_bought || false);
      }
    } catch (e) { console.error(e); }
  };

  const handleBuy = async () => {
    setBuying(true);
    try {
      const res = await fetch(`${API}/api/store/deal-of-the-day/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) { setBought(true); onRefresh(); }
      }
    } catch (e) { console.error(e); }
    setBuying(false);
  };

  if (!deal) return null;

  const h = Math.floor(expiresIn / 3600);
  const m = Math.floor((expiresIn % 3600) / 60);

  return (
    <div className="mb-5 bg-gradient-to-r from-[#C9A84C]/10 via-[#0a0e12] to-[#C9A84C]/5 border border-[#C9A84C]/30 rounded-xl p-4 relative overflow-hidden" data-testid="deal-of-the-day">
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A84C]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
      <div className="flex items-start gap-4 relative z-10">
        <div className="shrink-0 w-12 h-12 bg-[#C9A84C]/15 rounded-lg flex items-center justify-center border border-[#C9A84C]/30">
          <Sparkles size={20} className="text-[#C9A84C]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-['Outfit'] text-[#C9A84C] text-[9px] tracking-[0.2em]">MATT'S DEAL OF THE DAY</span>
            <span className="font-['Outfit'] text-[#8B1A1A] text-[9px] tracking-widest px-1.5 py-0.5 bg-[#8B1A1A]/10 border border-[#8B1A1A]/30 rounded">50% OFF</span>
          </div>
          <h3 className="font-['Outfit'] text-[#E8E0D0] text-base font-bold mt-1">{deal.name}</h3>
          {deal.desc && <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] mt-0.5">{deal.desc}</p>}
          {deal.stars && (
            <div className="flex gap-0.5 mt-1">
              {Array.from({ length: deal.stars }).map((_, i) => (
                <Star key={i} size={8} className="text-[#C9A84C] fill-[#C9A84C]" />
              ))}
            </div>
          )}
          <p className="font-['Cormorant_Garamond'] text-[#C9A84C]/50 italic text-xs mt-2">"{deal.matt_line}"</p>
        </div>
        <div className="shrink-0 text-right">
          <div className="mb-2">
            <span className="font-['Outfit'] text-[#E8E0D0]/25 text-sm line-through">{deal.original_price?.toLocaleString()}</span>
            <div className="flex items-center gap-1 justify-end">
              <Gem size={12} className="text-[#9F7AEA]" />
              <span className="font-['Outfit'] text-[#9F7AEA] text-lg font-bold">{deal.deal_price?.toLocaleString()}</span>
            </div>
          </div>
          {bought ? (
            <span className="font-['Outfit'] text-[#4ADE80] text-[10px] tracking-widest">PURCHASED</span>
          ) : deal.can_afford ? (
            <button onClick={handleBuy} disabled={buying}
              className="px-4 py-1.5 bg-[#C9A84C]/15 border border-[#C9A84C]/40 rounded text-[#C9A84C] font-['Outfit'] text-[10px] tracking-widest hover:bg-[#C9A84C]/25 disabled:opacity-30"
              data-testid="deal-buy-btn">
              {buying ? '...' : 'BUY NOW'}
            </button>
          ) : (
            <span className="font-['Outfit'] text-[#8B1A1A]/60 text-[9px] tracking-widest px-2 py-1 border border-[#8B1A1A]/20 rounded">
              NEED MORE CRYSTALS
            </span>
          )}
          <p className="font-['Outfit'] text-[#E8E0D0]/15 text-[8px] mt-1 tabular-nums">
            <Timer size={8} className="inline mr-0.5" />{h}h {m}m left
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN MATT STORE COMPONENT
// ============================================================
const MattStoreFull = ({ gameId, onClose, onRefresh }) => {
  const [categories, setCategories] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('daily_perks');
  const [currencies, setCurrencies] = useState({});
  const [showInbox, setShowInbox] = useState(false);

  useEffect(() => {
    loadCategories();
    loadCurrencies();
  }, [gameId]);

  const loadCategories = async () => {
    try {
      const res = await fetch(`${API}/api/store/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories);
      }
    } catch (e) { console.error(e); }
  };

  const loadCurrencies = async () => {
    try {
      const res = await fetch(`${API}/api/store/currencies/${gameId}`);
      if (res.ok) setCurrencies(await res.json());
    } catch (e) { console.error(e); }
  };

  const handleRefresh = () => {
    loadCurrencies();
    if (onRefresh) onRefresh();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-[#060910] flex flex-col"
      data-testid="matt-store-full"
    >
      {/* Top currency bar */}
      <CurrencyBar currencies={currencies} onClose={onClose} onHomeClick={onClose} onInboxClick={() => setShowInbox(true)} />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <StoreSidebar
          categories={categories}
          activeSubTab={activeSubTab}
          onSelectSubTab={(tab) => { setActiveSubTab(tab); setShowInbox(false); }}
          newBadges={['daily_perks', 'event']}
        />

        {/* Main content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {showInbox ? (
            <InboxPanel gameId={gameId} onRefresh={handleRefresh} />
          ) : (
            <>
              {/* Deal of the Day — always at top */}
              <DealOfTheDay gameId={gameId} onRefresh={handleRefresh} />

          {/* Matt header for his special tab */}
          {activeSubTab === 'matts_special' && (
            <div className="flex items-center gap-3 mb-4 bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-lg p-3">
              <PackageOpen size={24} className="text-[#C9A84C]" />
              <div>
                <h3 className="font-['Cormorant_Garamond'] text-[#C9A84C] font-bold">MATT'S SPECIAL DROPS</h3>
                <p className="font-['Cormorant_Garamond'] text-[#E8E0D0]/40 italic text-sm">"I put these together myself. Best value in the game. Trust me."</p>
              </div>
            </div>
          )}

          <StoreContent activeSubTab={activeSubTab} gameId={gameId} currencies={currencies} onRefresh={handleRefresh} />
            </>
          )}
        </div>
      </div>

      {/* Bottom bar — crystal balance + VIP */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#080b0f] border-t border-[#C9A84C]/10">
        <div className="flex items-center gap-2">
          <Gem size={12} className="text-[#9F7AEA]" />
          <span className="font-['Outfit'] text-[#9F7AEA] text-xs font-semibold">{(currencies.water_crystals || 0).toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-2">
          <Crown size={12} className="text-[#C9A84C]/40" />
          <span className="font-['Outfit'] text-[#E8E0D0]/25 text-[9px] tracking-widest">VIP {currencies.vip_level || 0}</span>
          <span className="font-['Outfit'] text-[#E8E0D0]/15 text-[9px]">{(currencies.loyalty_points || 0).toLocaleString()} LP</span>
        </div>
      </div>
    </motion.div>
  );
};

export default MattStoreFull;
